
import React, { createContext, useContext, useState, useEffect } from 'react';
import { Content } from '@/types/content';
import { toast } from 'sonner';

type ListContextType = {
  myList: Content[];
  addToList: (content: Content) => void;
  removeFromList: (content: Content | string) => void;
  isInList: (content: Content | string) => boolean;
};

const ListContext = createContext<ListContextType | undefined>(undefined);

export const ListProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [myList, setMyList] = useState<Content[]>(() => {
    const savedList = localStorage.getItem('netflix-mylist');
    return savedList ? JSON.parse(savedList) : [];
  });

  // Save to localStorage whenever the list changes
  useEffect(() => {
    localStorage.setItem('netflix-mylist', JSON.stringify(myList));
  }, [myList]);

  const addToList = (content: Content) => {
    if (!isInList(content.id)) {
      setMyList((prevList) => [...prevList, content]);
      toast.success(`Added "${content.title}" to My List`);
    }
  };

  const removeFromList = (contentOrId: Content | string) => {
    const contentId = typeof contentOrId === 'string' ? contentOrId : contentOrId.id;
    const contentToRemove = myList.find((item) => item.id === contentId);
    setMyList((prevList) => prevList.filter((item) => item.id !== contentId));
    if (contentToRemove) {
      toast.success(`Removed "${contentToRemove.title}" from My List`);
    }
  };

  const isInList = (contentOrId: Content | string) => {
    const contentId = typeof contentOrId === 'string' ? contentOrId : contentOrId.id;
    return myList.some((item) => item.id === contentId);
  };

  return (
    <ListContext.Provider value={{ myList, addToList, removeFromList, isInList }}>
      {children}
    </ListContext.Provider>
  );
};

// Export both names for the same hook for backward compatibility
export const useList = (): ListContextType => {
  const context = useContext(ListContext);
  if (context === undefined) {
    throw new Error('useList must be used within a ListProvider');
  }
  return context;
};

// Add this export to match the import in VideoDetail.tsx
export const useListContext = useList;
